import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import client from "../lib/api";
import { PushToggle } from "./PushToggle";
import {
  LayoutDashboard, TrendingUp, Bitcoin, Radio, Bot, Newspaper, CalendarClock,
  BookText, Users, Contact, Handshake, Send, PenSquare,
  GitBranch, ShieldCheck, UsersRound, Settings, LogOut, Menu, X, Bell, Coins, AlertTriangle, MessageSquare, Vault, MessageCircle, Gauge
} from "lucide-react";

export const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, mod: "dashboard" },
  { to: "/assistant", label: "AI Assistant", icon: Bot, mod: "performance" },
  { to: "/gold", label: "XAUUSD Intelligence", icon: Coins, mod: "gold" },
  { to: "/btc", label: "BTCUSD Intelligence", icon: Bitcoin, mod: "btc" },
  { to: "/ai", label: "AI Research", icon: Bot, mod: "ai" },
  { to: "/news", label: "News & Impact", icon: Newspaper, mod: "news" },
  { to: "/calendar", label: "Economic Calendar", icon: CalendarClock, mod: "calendar" },
  { to: "/send-signal", label: "Send Signal", icon: Send, mod: "performance" },
  { to: "/follow-up", label: "Follow-up Msg", icon: MessageSquare, mod: "performance" },
  { to: "/broker-vault", label: "Broker Reports", icon: Vault, mod: "performance" },
  { to: "/vip-trades", label: "VIP Trades", icon: TrendingUp, mod: "vip_trades" },
  { to: "/journal", label: "Research Journal", icon: BookText, mod: "journal" },
  { to: "/clients", label: "Clients", icon: Users, mod: "clients" },
  { to: "/crm", label: "CRM / Leads", icon: Contact, mod: "crm" },
  { to: "/brokers", label: "Broker Partners", icon: Handshake, mod: "brokers" },
  { to: "/telegram", label: "Telegram", icon: Send, mod: "telegram" },
  { to: "/whatsapp", label: "WhatsApp", icon: MessageCircle, mod: "telegram" },
  { to: "/content", label: "Content Studio", icon: PenSquare, mod: "content" },
  { to: "/content-hub", label: "Blog, Chart AI & Alerts", icon: Radio, mod: "content" },
  { to: "/analytics", label: "Analytics", icon: GitBranch, mod: "analytics" },
  { to: "/team", label: "Team", icon: UsersRound, mod: "team" },
  { to: "/partners", label: "Team Partners", icon: Handshake, mod: "team" },
  { to: "/workforce", label: "Lead Workforce", icon: Gauge, mod: "team" },
  { to: "/audit", label: "Audit Logs", icon: ShieldCheck, mod: "audit" },
  { to: "/settings", label: "Settings", icon: Settings, mod: "settings" },
];

export default function Layout({ children, title }) {
  const { user, logout, canAccess } = useAuth();
  const [open, setOpen] = useState(false);
  const [brand, setBrand] = useState("Fateh FX");
  const [notifs, setNotifs] = useState([]);
  const [showNotif, setShowNotif] = useState(false);
  const [aiHealth, setAiHealth] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    client.get("/settings").then(({ data }) => setBrand(data.brand_name || "Fateh FX")).catch(() => {});
    client.get("/notifications").then(({ data }) => setNotifs(data)).catch(() => {});
    client.get("/system/ai-health").then(({ data }) => setAiHealth(data)).catch(() => {});
  }, []);

  const hidden = user?.hidden_menu || [];
  const items = NAV.filter((n) => canAccess(n.mod) && !hidden.includes(n.to));
  const unread = notifs.filter((n) => !n.read).length;

  return (
    <div className="min-h-screen flex grain" style={{ background: "var(--app-bg)" }}>
      {/* Sidebar */}
      <aside className={`fixed lg:static z-40 h-screen w-64 shrink-0 flex flex-col border-r border-[color:var(--border-subtle)] transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`} style={{ background: "var(--surface)" }}>
        <div className="h-16 flex items-center gap-2 px-5 border-b border-[color:var(--border-subtle)]">
          <div className="w-8 h-8 flex items-center justify-center bg-gold text-black font-head font-bold text-sm">F</div>
          <div className="leading-tight">
            <div className="font-head font-bold text-white tracking-tight">{brand}</div>
            <div className="text-[10px] text-slate-500 uppercase tracking-widest">Command Center</div>
          </div>
          <button className="ml-auto lg:hidden text-slate-400" onClick={() => setOpen(false)}><X size={18} /></button>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-2">
          {items.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.mod}`} onClick={() => setOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2 mb-0.5 text-sm transition-colors duration-200 border-l-2 ${isActive ? "border-gold text-white bg-white/5" : "border-transparent text-slate-400 hover:text-white hover:bg-white/5"}`}>
              <n.icon size={17} strokeWidth={1.8} />{n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-[color:var(--border-subtle)]">
          <PushToggle />
          <button onClick={logout} data-testid="logout-btn" className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-400 hover:text-bear transition-colors duration-200">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 sticky top-0 z-20 glass flex items-center gap-3 px-4 md:px-6">
          <button className="lg:hidden text-slate-300" onClick={() => setOpen(true)} data-testid="menu-btn"><Menu size={20} /></button>
          <h1 className="text-lg font-head font-semibold tracking-tight text-white truncate">{title}</h1>
          <div className="ml-auto flex items-center gap-3">
            <div className="relative">
              <button data-testid="notif-btn" onClick={() => setShowNotif((s) => !s)} className="relative text-slate-400 hover:text-white transition-colors duration-200 p-2">
                <Bell size={18} />
                {unread > 0 && <span className="absolute top-1 right-1 w-4 h-4 text-[9px] flex items-center justify-center bg-bear text-white rounded-full">{unread}</span>}
              </button>
              {showNotif && (
                <div className="fixed left-3 right-3 top-16 sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-80 surface z-50 max-h-96 overflow-y-auto">
                  <div className="px-4 py-2 border-b border-[color:var(--border-subtle)] text-xs uppercase tracking-widest text-slate-500">Notifications</div>
                  {notifs.map((n) => (
                    <div key={n.id} className="px-4 py-2.5 border-b border-[color:var(--border-subtle)] text-sm text-slate-300 hover:bg-white/5">{n.message}</div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 pl-3 border-l border-[color:var(--border-subtle)]">
              <div className="w-8 h-8 bg-gold text-black flex items-center justify-center font-head font-bold text-sm">{user?.name?.[0] || "U"}</div>
              <div className="hidden md:block leading-tight">
                <div className="text-sm text-white">{user?.name}</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-wider">{user?.role?.replace("_", " ")}</div>
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-6 w-full max-w-screen-2xl mx-auto">
          {aiHealth && aiHealth.ok === false && (
            <div data-testid="ai-health-banner" className="mb-4 flex items-start gap-3 border border-[#f59e0b]/40 bg-[#f59e0b]/10 px-4 py-3">
              <AlertTriangle size={18} className="text-[#f59e0b] mt-0.5 shrink-0" />
              <div className="text-sm text-amber-100/90">
                <span className="font-semibold text-[#f59e0b]">AI service degraded.</span>{" "}
                {aiHealth.message || "Signal reposts are using a basic fallback."}
                {aiHealth.reason === "credit" && (
                  <span className="block text-xs text-amber-200/70 mt-1">Profile → Manage plan → Universal Key → Add Balance (or enable auto top-up).</span>
                )}
              </div>
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  );
}
